"use client"
import React, { useState, useEffect, useRef } from "react"
import { motion, useInView } from "framer-motion"
import { standardScrollConfig } from "@/utils/animations"
import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { BlogLogoImage } from "@/app/(app)/(blog)/components/BlogLogo"

/**
 * Type definition for a featured publication
 */
type FeaturedPub = {
  id: string
  name: string
  url: string
  imageUrl?: string
  description: string
}

/**
 * FeaturedPublications component displays a grid of publications with one featured publication
 * Allows interactive swapping of featured publication on hover
 * @param publications - Array of publication objects to display
 */
export default function FeaturedPublications({
  publications,
}: {
  publications: Array<FeaturedPub>
}) {
  // Need at least 19 publications (9 left + 1 featured + 9 right)
  if (publications.length < 19) {
    console.warn(
      `Expected at least 19 publications, but got ${publications.length}`
    )
  }

  // Fixed featured index (middle position)
  const FEATURED_INDEX = Math.min(9, Math.floor(publications.length / 2))

  // State to track the current order of publications
  const [swappedPublications, setSwappedPublications] = useState<FeaturedPub[]>(
    [...publications]
  )

  // State to track which publication is currently being hovered
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  // State to track if we're currently animating a swap
  const [isSwapping, setIsSwapping] = useState(false)

  // Flag to track if a swap is allowed (only after mouse movement)
  const [swapAllowed, setSwapAllowed] = useState(true)

  // Store the mouse position during the last swap
  const lastSwapPositionRef = useRef<{ x: number; y: number } | null>(null)

  // Ref to store hover timeouts
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Function to check if mouse has moved enough to allow a new swap
  const hasMouseMovedEnough = (currentX: number, currentY: number) => {
    if (!lastSwapPositionRef.current) return true

    const { x: lastX, y: lastY } = lastSwapPositionRef.current
    // Calculate distance using Pythagorean theorem
    const distance = Math.sqrt(
      Math.pow(currentX - lastX, 2) + Math.pow(currentY - lastY, 2)
    )

    // Require at least 10px of movement
    return distance >= 10
  }

  // Update mouse position and check for movement
  const updateMousePosition = (e: React.MouseEvent) => {
    if (!swapAllowed && hasMouseMovedEnough(e.clientX, e.clientY)) {
      setSwapAllowed(true)
    }
  }

  // Function to handle publication hover
  const handleHover = (index: number, e: React.MouseEvent) => {
    // If we're already swapping or swap is not allowed, don't do anything
    if (isSwapping || !swapAllowed) return

    // Update mouse position for movement check
    updateMousePosition(e)

    setHoveredIndex(index)

    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    // Set new timeout to swap after hover animation completes (1 second)
    hoverTimeoutRef.current = setTimeout(() => {
      setIsSwapping(true)

      // After another small delay for animation, swap the publications
      setTimeout(() => {
        // Store the current mouse position
        lastSwapPositionRef.current = { x: e.clientX, y: e.clientY }

        // Swap the featured publication with the hovered one
        const newPublications = [...swappedPublications]

        // Swap the positions in the array
        const temp = newPublications[FEATURED_INDEX]
        newPublications[FEATURED_INDEX] = newPublications[index]
        newPublications[index] = temp

        setSwappedPublications(newPublications)
        setIsSwapping(false)
        setHoveredIndex(null)

        // Disallow swap until mouse moves
        setSwapAllowed(false)
      }, 500) // Animation time for the swap
    }, 1000) // Wait for hover animation to complete
  }

  // Function to handle mouse leave
  const handleMouseLeave = () => {
    setHoveredIndex(null)
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  // Get the current featured publication
  const featuredPublication = swappedPublications[FEATURED_INDEX]

  // Re-arrange publications based on the currently featured index
  const leftPublications = []
  const rightPublications = []

  for (let i = 0; i < swappedPublications.length; i++) {
    if (i === FEATURED_INDEX) continue // Skip the featured publication

    if (leftPublications.length < 9) {
      leftPublications.push({
        pub: swappedPublications[i],
        originalIndex: i,
        isExpanding: isSwapping && hoveredIndex === i,
      })
    } else if (rightPublications.length < 9) {
      rightPublications.push({
        pub: swappedPublications[i],
        originalIndex: i,
        isExpanding: isSwapping && hoveredIndex === i,
      })
    }
  }

  // If there are not enough publications, pad with null values
  while (leftPublications.length < 9) {
    leftPublications.push({ pub: null, originalIndex: -1, isExpanding: false })
  }
  while (rightPublications.length < 9) {
    rightPublications.push({ pub: null, originalIndex: -1, isExpanding: false })
  }

  const isCollapsing = isSwapping && hoveredIndex !== null

  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, standardScrollConfig)

  return (
    <div className="container">
      <motion.div
        ref={sectionRef}
        className="flex flex-col md:flex-row transition-all duration-500 ease-in-out"
        onMouseMove={updateMousePosition}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Left grid - 9 small publications (3x3) */}
        <motion.div
          className="grid grid-cols-3 gap-4 pb-4 lg:pb-0 pr-0 lg:pr-4 w-full"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.05,
                delayChildren: 0.2,
              },
            },
          }}
        >
          {leftPublications.map((item, idx) =>
            item.pub ? (
              <motion.div
                key={item.pub.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <PublicationCard
                  publication={item.pub}
                  isExpanding={item.isExpanding}
                  onHover={(e) => handleHover(item.originalIndex, e)}
                  onMouseLeave={handleMouseLeave}
                  swapAllowed={swapAllowed}
                />
              </motion.div>
            ) : (
              <EmptyCard key={`empty-left-${idx}`} />
            )
          )}
        </motion.div>

        {/* Center - 1 featured publication */}
        <motion.div
          className={`bg-wood p-12 w-full rounded-xl transition-all duration-300 ${
            isCollapsing ? "opacity-80" : "scale-100"
          }`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={
            isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }
          }
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <FeaturedPublicationCard
            publication={featuredPublication}
            isCollapsing={isCollapsing}
          />
        </motion.div>

        {/* Right grid - 9 small publications (3x3) */}
        <motion.div
          className="grid grid-cols-3 gap-4 pt-4 lg:pt-0 lg:pl-4 w-full"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.05,
                delayChildren: 0.5,
              },
            },
          }}
        >
          {rightPublications.map((item, idx) =>
            item.pub ? (
              <motion.div
                key={item.pub.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                    },
                  },
                }}
              >
                <PublicationCard
                  publication={item.pub}
                  isExpanding={item.isExpanding}
                  onHover={(e) => handleHover(item.originalIndex, e)}
                  onMouseLeave={handleMouseLeave}
                  swapAllowed={swapAllowed}
                />
              </motion.div>
            ) : (
              <EmptyCard key={`empty-right-${idx}`} />
            )
          )}
        </motion.div>
      </motion.div>
    </div>
  )
}

/**
 * Empty card component for padding the grid
 */
const EmptyCard: React.FC = () => {
  return <div className="aspect-square bg-transparent" />
}

/**
 * Regular publication card component showing just the logo
 * With hover animation and expansion capability
 */
const PublicationCard: React.FC<{
  publication: FeaturedPub
  isExpanding?: boolean
  onHover: (e: React.MouseEvent) => void
  onMouseLeave: () => void
  swapAllowed?: boolean
}> = ({
  publication,
  isExpanding = false,
  onHover,
  onMouseLeave,
  swapAllowed = true,
}) => {
  return (
    <Card
      className={`
        bg-linen border-none shadow-none aspect-square relative overflow-hidden group
        transition-all duration-300 ease-in-out
        ${isExpanding ? "scale-110 z-20 shadow-lg" : "scale-100 z-10"}
      `}
      onMouseEnter={onHover}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={`absolute inset-0 bg-wood/10 rounded-lg scale-0 origin-center transition-transform duration-1000 ease-out ${swapAllowed ? "group-hover:scale-100" : ""}`}
      />

      <CardContent className="p-4 flex justify-center items-center h-full relative z-10">
        {publication.imageUrl ? (
          <BlogLogoImage
            src={publication.imageUrl}
            alt={`${publication.name} logo`}
            size={64}
            className={`bg-white w-14 h-14 transition-all duration-300 ${
              isExpanding ? "scale-125" : "scale-100"
            }`}
          />
        ) : (
          <div
            className={`
            w-16 h-16 bg-stone-200 flex items-center justify-center text-stone-500 font-bold
            transition-all duration-300 
            ${isExpanding ? "scale-125" : "scale-100"}
          `}
          >
            {publication.name.slice(0, 2).toUpperCase()}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Featured publication card with more details
 */
const FeaturedPublicationCard: React.FC<{
  publication: FeaturedPub
  isCollapsing?: boolean
}> = ({ publication, isCollapsing = false }) => {
  // Extract initials for avatar fallback
  const initials = publication.name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  return (
    <div
      className={`
      flex flex-col items-center justify-center h-full text-white
      transition-all duration-300 ease-in-out
    `}
    >
      <div
        className={`
        mb-4 w-32 h-32 transition-all duration-300
        ${isCollapsing ? "scale-90 opacity-90" : "scale-100 opacity-100"}
      `}
      >
        {publication.imageUrl ? (
          <BlogLogoImage
            src={publication.imageUrl}
            alt={`${publication.name} logo`}
            size={128}
            className="bg-white w-full h-full"
          />
        ) : (
          <div className="w-32 h-32 bg-sky-200 flex items-center justify-center text-sky-800 font-bold text-3xl rounded-xl">
            {initials}
          </div>
        )}
      </div>

      <h2
        className={`
        text-2xl font-ivyora mb-2 transition-all duration-300
        ${isCollapsing ? "opacity-80" : "opacity-100"}
      `}
      >
        {publication.name}
      </h2>

      <div
        className={`
        flex items-center mb-3 transition-all duration-300
        ${isCollapsing ? "opacity-70" : "opacity-100"}
      `}
      >
        <span className="text-stone-400 text-sm"></span>
      </div>

      <p
        className={`
        text-stone-400 text-sm text-center mb-4 transition-all duration-300 min-h-[3rem]
        ${isCollapsing ? "opacity-60" : "opacity-100"}
      `}
      >
        {publication.description}
      </p>

      <Button
        asChild
        variant="outline"
        className={`
          transition-all duration-300
          ${isCollapsing ? "opacity-50 scale-95" : "opacity-100 scale-100"}
        `}
      >
        <Link href={publication.url}>Read publication</Link>
      </Button>
    </div>
  )
}
